import React from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import RequireAuth from "./AuthRouter";
import RequireRole from "./RequireRole";
import Container from "./Container";
import { ADMIN_OR_OPERATOR, WITH_CLIENT_ACCESS } from "../utils/permissions";

import LoginPage from "../pages/LoginPage";
import NotFoundPage from "../pages/NotFoundPage";
import MyProfilePage from "../pages/MyProfilePage";
import TodayPage from "../pages/TodayPage";
import PipelinePage from "../pages/PipelinePage";
import QueuePage from "../pages/QueuePage";
import TasksPage from "../pages/TasksPage";
import FunnelPage from "../pages/FunnelPage";
import EmailsPage from "../pages/EmailsPage";
import EmailComposerPage from "../pages/EmailComposerPage";
import EmailReportPage from "../pages/EmailReportPage";
import ClientDetailPage from "../pages/ClientDetailPage";
import SuggestionsPage from "../pages/SuggestionsPage";
import EventsPage from "../pages/EventsPage";
import EventPage from "../pages/EventPage";

import DashboardRouter from "../components/DashboardRouter";
import PortfolioDashboard from "../components/PortfolioDashboard";
import CustomersTable from "../components/tables/CustomersTable";
import ProfessionalsTable from "../components/tables/ProfessionalsTable";
import PaymentsTable from "../components/tables/PaymentsTable";
import AnnouncementsTable from "../components/tables/AnnouncementsTable";
import CouponsTable from "../components/tables/CouponsTable";
import BackofficeUsersTable from "../components/tables/BackofficeUsersTable";
import TrainingsTable from "../components/tables/Trainingstable";

/*
  Rotas do back-office.

  Três mudanças em relação ao mapa antigo:

  1. **A home virou "Hoje"**, a tela de trabalho, e não mais o dashboard de
     gráficos (que passou para /metricas). Era a diferença entre abrir o
     painel e saber o que fazer, ou abrir e ter que decidir.

  2. **Caminhos em português**, iguais ao que o menu mostra. O mapa anterior
     misturava `/customers` e `/comunicados` na mesma barra — um link
     compartilhado por quem atende não devia exigir tradução mental.

  3. **Os caminhos antigos redirecionam** (ver REDIRECTS). Links de
     /customers e /dashboard estão colados em conversa de equipe e em aba
     aberta; quebrar todos de uma vez transformaria a reformulação em
     "o painel parou".
*/

const REDIRECTS = [
  ["/dashboard", "/metricas"],
  ["/customers", "/clientes"],
  ["/professionals", "/usuarios"],
  ["/payments", "/pagamentos"],
  ["/coupons", "/cupons"],
  ["/team", "/acessos"],
  ["/training", "/treinamentos"],
  ["/profile", "/perfil"],
];

const AppRouter = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          path="/"
          element={
            <RequireAuth>
              <Container>
                <Outlet />
              </Container>
            </RequireAuth>
          }
        >
          <Route index element={<TodayPage />} />

          {/* Trabalho */}
          <Route path="pipeline" element={<PipelinePage />} />
          <Route path="filas/:id" element={<QueuePage />} />
          <Route path="tarefas" element={<TasksPage />} />
          <Route
            path="eventos"
            element={
              <RequireRole roles={ADMIN_OR_OPERATOR}>
                <EventsPage />
              </RequireRole>
            }
          />
          <Route
            path="eventos/:id"
            element={
              <RequireRole roles={ADMIN_OR_OPERATOR}>
                <EventPage />
              </RequireRole>
            }
          />

          {/* Carteira */}
          <Route
            path="clientes"
            element={
              <RequireRole roles={WITH_CLIENT_ACCESS}>
                <CustomersTable />
              </RequireRole>
            }
          />
          <Route
            path="clientes/:id"
            element={
              <RequireRole roles={WITH_CLIENT_ACCESS}>
                <ClientDetailPage />
              </RequireRole>
            }
          />
          <Route
            path="carteira"
            element={
              <RequireRole roles={WITH_CLIENT_ACCESS}>
                <PortfolioDashboard />
              </RequireRole>
            }
          />

          {/* E-mail: campanhas, automações e descadastros */}
          <Route path="emails" element={<EmailsPage />} />
          <Route path="emails/nova" element={<EmailComposerPage />} />
          <Route path="emails/:id" element={<EmailReportPage />} />
          <Route path="emails/:id/editar" element={<EmailComposerPage />} />

          {/* Análise */}
          <Route
            path="funil"
            element={
              <RequireRole roles={WITH_CLIENT_ACCESS}>
                <FunnelPage />
              </RequireRole>
            }
          />
          <Route
            path="metricas"
            element={
              <RequireRole>
                <DashboardRouter />
              </RequireRole>
            }
          />

          {/* Gestão */}
          <Route
            path="usuarios"
            element={
              <RequireRole roles={ADMIN_OR_OPERATOR}>
                <ProfessionalsTable />
              </RequireRole>
            }
          />
          <Route
            path="pagamentos"
            element={
              <RequireRole roles={ADMIN_OR_OPERATOR}>
                <PaymentsTable />
              </RequireRole>
            }
          />
          <Route path="cupons" element={<CouponsTable />} />
          <Route path="sugestoes" element={<SuggestionsPage />} />
          <Route
            path="comunicados"
            element={
              <RequireRole roles={ADMIN_OR_OPERATOR}>
                <AnnouncementsTable />
              </RequireRole>
            }
          />
          <Route path="treinamentos" element={<TrainingsTable />} />

          {/* Administração */}
          <Route
            path="acessos"
            element={
              <RequireRole>
                <BackofficeUsersTable />
              </RequireRole>
            }
          />

          <Route path="perfil" element={<MyProfilePage />} />

          {REDIRECTS.map(([from, to]) => (
            <Route
              key={from}
              path={from.slice(1)}
              element={<Navigate to={to} replace />}
            />
          ))}

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
